import FreeText from '@algbra/ui/components/FreeText/FreeText'
import { Container } from '@algbra/ui/components/Grid/Grid'
import Heading from '@algbra/ui/components/Heading/Heading'
import Whitespace from '@algbra/ui/components/Whitespace/Whitespace'
import { Link } from 'gatsby'
import React from 'react'
import styles from './ComparePlans.module.scss'
import { ReactComponent as CheckIcon } from '../../images/check.svg'
import { ReactComponent as FscsLogo } from '../../images/fscs-logo.svg'
import { ReactComponent as NoCheckIcon } from '../../images/noCheck.svg'
import { StaticImage } from 'gatsby-plugin-image'

export type ComparePlansItemProps = {
  id: string
  title: string
  description?: string
  standardValue?: string
  borderlessValue?: string
}

export type ComparePlansGroupProps = {
  id: string
  title: string
  compareListItem?: ComparePlansItemProps[]
}

export type ZoneComparePlansProps = {
  __typename: 'StrapiGQL_ComponentZonesComparePlans'
  id: string
  compareList?: ComparePlansGroupProps[]
}

function getDisplayValue(value?: string) {
  return value?.trim() || '—'
}

function renderValue(value?: string) {
  const displayValue = getDisplayValue(value)
  const normalizedValue = displayValue.toLowerCase()

  if (normalizedValue === 'check') {
    return <CheckIcon className={styles.valueIcon} aria-label="Included" />
  }

  if (normalizedValue === 'nocheck') {
    return (
      <NoCheckIcon className={styles.valueIcon} aria-label="Not included" />
    )
  }

  return <span className={styles.value}>{displayValue}</span>
}

export default function ZoneComparePlans({
  compareList,
}: ZoneComparePlansProps): JSX.Element | null {
  if (!compareList?.length) {
    return null
  }

  return (
    <Whitespace type="lg">
      <Container>
        <Heading type="md" className={styles.sectionTitle}>
          See what’s included
        </Heading>
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <div className={styles.headerSpacer} />
            <div className={styles.planHeader}>
              <StaticImage
                src="../../images/standard-card.png"
                alt="Standard card"
                layout="constrained"
                width={76}
                height={48}
                quality={100}
                placeholder="blurred"
                loading="lazy"
                className={styles.planCardImage}
              />
              <Heading level={3} type="sm" className={styles.planTitle}>
                Standard
              </Heading>
              <FreeText type="sm" className={styles.planPrice}>
                Free
              </FreeText>
              <Link
                to="/features/multi-currency/"
                className={`${styles.planButton} ${styles.standardButton}`}
              >
                Get started for free
              </Link>
            </div>
            <div className={styles.planHeader}>
              <StaticImage
                src="../../images/borderless-card-small.png"
                alt="Borderless card"
                layout="constrained"
                width={76}
                height={48}
                quality={100}
                placeholder="blurred"
                loading="lazy"
                className={styles.planCardImage}
              />
              <Heading level={3} type="sm" className={styles.planTitle}>
                Borderless
              </Heading>
              <FreeText type="sm" className={styles.planPrice}>
                £10/month
              </FreeText>
              <Link
                to="/features/borderless-account/"
                className={`${styles.planButton} ${styles.borderlessButton}`}
              >
                Open a borderless account
              </Link>
            </div>
          </div>

          {compareList.map(group => {
            if (!group.compareListItem?.length) {
              return null
            }

            return (
              <div key={group.id} className={styles.group}>
                <FreeText type="md" className={styles.groupTitle}>
                  {group.title}
                </FreeText>

                <div className={styles.rows}>
                  {group.compareListItem.map(item => (
                    <div key={item.id} className={styles.row}>
                      <div className={styles.topicCell}>
                        <div className={styles.itemTitleRow}>
                          <FreeText type="sm" className={styles.itemTitle}>
                            {item.title}
                          </FreeText>
                          {item.title === 'Ethical Saver Cubes' && (
                            <FscsLogo
                              className={styles.fscsLogo}
                              aria-label="FSCS logo"
                            />
                          )}
                        </div>

                        {item.description && (
                          <FreeText
                            as="p"
                            type="sm"
                            className={styles.itemDescription}
                          >
                            {item.description}
                          </FreeText>
                        )}
                      </div>

                      <div className={styles.valueCell}>
                        <span className={styles.mobileLabel}>Standard</span>
                        {renderValue(item.standardValue)}
                      </div>

                      <div className={styles.valueCell}>
                        <span className={styles.mobileLabel}>Borderless</span>
                        {renderValue(item.borderlessValue)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </section>
      </Container>
    </Whitespace>
  )
}
